import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Container, Typography, Box, Link, Paper, Grid, IconButton, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Register = () => {
  const { register: registerUser, isRegistering } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  const onSubmit = async (data) => {
    try {
      await registerUser(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
      py: { xs: 4, md: 8 },
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 0% 0%, rgba(92,15,47,0.05) 0%, transparent 50%)',
        zIndex: 0
      }
    }}>
      {/* Navigation Buttons */}
      <Box sx={{ 
        position: 'absolute',
        top: 20,
        left: 20,
        zIndex: 10,
        display: 'flex',
        gap: 2
      }}>
        <IconButton 
          onClick={() => navigate('/')}
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { bgcolor: 'white' },
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        >
          <HomeIcon />
        </IconButton>
        <IconButton 
          onClick={() => navigate(-1)}
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { bgcolor: 'white' },
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              textAlign: 'center',
              mb: { xs: 4, md: 0 }
            }}>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 800,
                  color: '#333',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Find Your Perfect Match
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#666',
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}
              >
                Join millions of happy couples who found their soulmate through our trusted platform
              </Typography>
              <Box sx={{ 
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 3,
                mb: 4
              }}>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#5c0f2f',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.1)',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)'
                  }
                }}>
                  <VerifiedUserIcon />
                  <Typography>Verified Profiles</Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#5c0f2f',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.1)',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)'
                  }
                }}>
                  <SecurityIcon />
                  <Typography>Secure Platform</Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#5c0f2f',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.1)',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)'
                  }
                }}>
                  <SearchIcon />
                  <Typography>Smart Matching</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 3, md: 4 },
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}
            >
              <Typography 
                component="h1" 
                variant="h4" 
                sx={{ 
                  textAlign: 'center',
                  mb: 3,
                  fontWeight: 700,
                  color: '#333',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '50px',
                    height: '3px',
                    background: 'linear-gradient(90deg, #5c0f2f, #7d1f4f)',
                    borderRadius: '2px'
                  }
                }}
              >
                Create Account
              </Typography>
              {error && (
                <Typography 
                  color="error" 
                  sx={{ 
                    textAlign: 'center',
                    mb: 2,
                    p: 1,
                    bgcolor: 'rgba(255, 0, 0, 0.1)',
                    borderRadius: '4px'
                  }}
                >
                  {error}
                </Typography>
              )}
              <Box 
                component="form" 
                onSubmit={handleSubmit(onSubmit)} 
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      autoFocus
                      {...register('first_name', { required: 'First name is required' })}
                      error={!!errors.first_name}
                      helperText={errors.first_name?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#5c0f2f',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#5c0f2f',
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#5c0f2f'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      {...register('last_name', { required: 'Last name is required' })}
                      error={!!errors.last_name}
                      helperText={errors.last_name?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#5c0f2f',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#5c0f2f',
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#5c0f2f'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  {...register('email', { required: 'Email is required' })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Mobile Number"
                  {...register('mobile', { required: 'Mobile number is required' })}
                  error={!!errors.mobile}
                  helperText={errors.mobile?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    {...register('gender', { required: 'Gender is required' })}
                    error={!!errors.gender}
                    label="Gender"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#5c0f2f',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#5c0f2f',
                        }
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#5c0f2f'
                      }
                    }}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                  </Select>
                  {errors.gender && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.gender.message}
                    </Typography>
                  )}
                </FormControl>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  {...register('password_confirmation', {
                    required: 'Please confirm your password',
                    validate: (value) =>
                      value === watch('password') || 'Passwords do not match',
                  })}
                  error={!!errors.password_confirmation}
                  helperText={errors.password_confirmation?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
                <Button 
                  type="submit" 
                  fullWidth 
                  variant="contained" 
                  disabled={isRegistering}
                  sx={{ 
                    mt: 2,
                    py: 1.5,
                    background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #4d0d28, #601233)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(92,15,47,0.3)'
                    },
                    '&:disabled': {
                      background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                      opacity: 0.7
                    },
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    borderRadius: '8px',
                    transition: 'all 0.3s'
                  }}
                >
                  {isRegistering ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      Creating Account...
                    </Box>
                  ) : (
                    'Create Account'
                  )}
                </Button>
                <Box sx={{ 
                  textAlign: 'center',
                  mt: 2
                }}>
                  <Link 
                    href="/login-page" 
                    sx={{ 
                      color: '#5c0f2f',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      },
                      transition: 'all 0.3s'
                    }}
                  >
                    Already have an account? Sign in
                  </Link>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Register;